import { AlertTriangle, CheckCircle2, Clock, PauseCircle, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  AttemptOutcome,
  PostReach,
  type DeadLetterJobLike,
  type OutboxAttemptDto,
} from '@shared/posting/outboxStatus';

/**
 * The expanded diagnosis for one dead-lettered job (86d3w2jrj FE-4).
 *
 * The table row can only show `lastError` — the LAST message — which is often the least informative
 * thing that happened. A job that deferred fifteen times and then failed once is telling you about
 * lane contention, not about itself, and the attempt rows recording that have been written since the
 * outbox shipped without ever reaching a screen. Answering "what actually happened to this job"
 * meant querying the database by hand.
 *
 * Three things are surfaced, in the order an operator needs them:
 *  1. How far it got toward NetSuite — this decides whether retrying is safe or a reconciliation.
 *  2. The structured failure detail, when a handler supplied one.
 *  3. The full attempt timeline, INCLUDING defers.
 */

/** What the reach value means for the operator's next move, not what it is called internally. */
const REACH_COPY: Record<number, { tone: 'success' | 'warning' | 'destructive'; text: string }> = {
  [PostReach.Unknown]: {
    tone: 'warning',
    text: 'How far this got is unknown — treat a retry as a reconciliation, not a re-post.',
  },
  [PostReach.NotReached]: {
    tone: 'success',
    text: 'Nothing reached NetSuite. Retrying posts for the first time; abandoning it loses nothing.',
  },
  [PostReach.Ambiguous]: {
    tone: 'warning',
    text: 'A record MAY exist in NetSuite. Retry — the handler adopts it by idempotency key. Do not abandon.',
  },
  [PostReach.Reached]: {
    tone: 'destructive',
    text: 'A record DOES exist in NetSuite. Retry to reconcile it; never abandon or re-post.',
  },
};

const OUTCOME_META: Record<
  number,
  { label: string; icon: typeof CheckCircle2; className: string }
> = {
  [AttemptOutcome.Succeeded]: { label: 'Succeeded', icon: CheckCircle2, className: 'text-success' },
  [AttemptOutcome.FailedTransient]: { label: 'Failed (will retry)', icon: AlertTriangle, className: 'text-warning' },
  [AttemptOutcome.FailedPermanent]: { label: 'Failed (permanent)', icon: XCircle, className: 'text-destructive' },
  [AttemptOutcome.Deferred]: { label: 'Deferred', icon: PauseCircle, className: 'text-info' },
};

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

interface DeadLetterDetailProps {
  job: DeadLetterJobLike;
}

export function DeadLetterDetail({ job }: DeadLetterDetailProps) {
  const reach = REACH_COPY[job.postReach] ?? REACH_COPY[PostReach.Unknown];
  const timeline: OutboxAttemptDto[] = job.timeline ?? [];
  const deferrals = timeline.filter((a) => a.outcome === AttemptOutcome.Deferred).length;

  return (
    <div className="space-y-4 bg-muted/30 p-4">
      {/* 1. reach — the safety question, answered first */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            NetSuite reach
          </span>
          <Badge
            variant={
              reach.tone === 'success' ? 'success' : reach.tone === 'warning' ? 'warning' : 'destructive'
            }
          >
            {reach.tone === 'success' ? 'Safe to retry' : 'Reconcile, don’t re-post'}
          </Badge>
          {job.netSuiteRecordId && (
            <span className="text-xs text-muted-foreground">
              record <code className="rounded bg-muted px-1.5 py-0.5">{job.netSuiteRecordId}</code>
            </span>
          )}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">{reach.text}</p>
      </div>

      {/* 2. structured detail, when a handler supplied one */}
      {job.failureDetailJson && (
        <div className="space-y-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Failure detail
          </span>
          <pre className="max-h-48 overflow-auto rounded-md border border-border bg-card p-2 text-xs text-foreground">
            {prettyJson(job.failureDetailJson)}
          </pre>
        </div>
      )}

      {/* 3. the timeline */}
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What happened
          </span>
          {deferrals > 0 && (
            <span className="text-xs text-muted-foreground">
              {deferrals} deferral{deferrals === 1 ? '' : 's'} — the lane was busy, not this job
            </span>
          )}
        </div>

        {timeline.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No attempt history was recorded for this job.
          </p>
        ) : (
          <ol className="space-y-1.5">
            {timeline.map((attempt, index) => {
              const meta = OUTCOME_META[attempt.outcome] ?? OUTCOME_META[AttemptOutcome.FailedTransient];
              const Icon = meta.icon;
              return (
                <li
                  key={`${attempt.startedAt}-${index}`}
                  className="flex items-start gap-2 rounded-md border border-border bg-card px-2.5 py-2"
                >
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.className}`} aria-hidden />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-xs font-medium text-foreground">
                        Attempt {attempt.attempt} · {meta.label}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden />
                        {new Date(attempt.startedAt).toLocaleString()}
                      </span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {formatDuration(attempt.durationMs)}
                      </span>
                    </div>
                    {attempt.errorMessage && (
                      <p className="break-words text-xs leading-relaxed text-muted-foreground">
                        {attempt.errorMessage}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
